import { Button } from "@shared/components/ui/Button";
import { useProfile } from "@shared/hooks/useProfile";
import { Check, Eye, EyeOff } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { pollService } from "../services/poll.service";
import { Poll, PollOwnerType, PollQuestion, RawPoll } from "../types/poll.types";
import {
  answeredCount,
  isAnswered,
  normalizePoll,
  percentageOf,
  pollHasEnded,
  toDate,
} from "../utils/poll.utils";
import { PollVotersModal } from "./PollVotersModal";

interface PollDisplayProps {
  poll: Poll | RawPoll;
  /** Only used to tell posts from spaces when something fails. */
  ownerType: PollOwnerType;
  /** Lets the list or page holding the poll keep the fresh copy. */
  onPollChange?: (poll: Poll) => void;
}

/**
 * One poll, with one or many questions. Each question is answered on its own,
 * takes one answer or several, and shows its results once answered.
 */
export function PollDisplay({ poll, ownerType, onPollChange }: PollDisplayProps) {
  const { user } = useProfile();
  const [localPoll, setLocalPoll] = useState<Poll>(() => normalizePoll(poll));
  const [selected, setSelected] = useState<Record<string, string[]>>({});
  const [editing, setEditing] = useState<Record<string, boolean>>({});
  const [votingQuestionId, setVotingQuestionId] = useState<string | null>(null);
  const [showVoters, setShowVoters] = useState(false);

  useEffect(() => {
    setLocalPoll(normalizePoll(poll));
  }, [poll]);

  const hasEnded = useMemo(() => pollHasEnded(localPoll), [localPoll]);
  const answered = answeredCount(localPoll);
  const total = localPoll.questions.length;
  const namesVisible = localPoll.voterVisibility === "everyone";

  const pickedOn = (question: PollQuestion): string[] =>
    selected[question.id] ?? question.userVotedOptionIds ?? [];

  const toggleOption = (question: PollQuestion, optionId: string) => {
    setSelected((current) => {
      const picked = current[question.id] ?? question.userVotedOptionIds ?? [];

      if (!question.allowMultiple) {
        return { ...current, [question.id]: [optionId] };
      }

      return {
        ...current,
        [question.id]: picked.includes(optionId)
          ? picked.filter((id) => id !== optionId)
          : [...picked, optionId],
      };
    });
  };

  const handleVote = async (question: PollQuestion) => {
    const optionIds = pickedOn(question);
    if (!user || hasEnded || !localPoll.id) return;
    if (!question.allowMultiple && optionIds.length !== 1) return;

    try {
      setVotingQuestionId(question.id);
      const updated = await pollService.vote(localPoll.id, question.id, optionIds);

      setLocalPoll(updated);
      setSelected((current) => ({ ...current, [question.id]: [] }));
      setEditing((current) => ({ ...current, [question.id]: false }));
      onPollChange?.(updated);

      toast.success(
        total > 1 && answeredCount(updated) < total
          ? "Answer saved. One question to go!"
          : "Your vote has been recorded!",
      );
    } catch (error) {
      console.error(`Error voting on ${ownerType} poll:`, error);
      toast.error("Failed to submit your vote. Please try again.");
    } finally {
      setVotingQuestionId(null);
    }
  };

  return (
    <div className="mt-4 bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
      <div className="flex items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400 mb-3">
        <span>
          {total > 1 && (
            <>
              {total} questions
              {!hasEnded && ` · ${answered} of ${total} answered`}
              {" · "}
            </>
          )}
          {namesVisible ? "Votes are not anonymous" : "Anonymous poll"}
        </span>
        {namesVisible ? (
          <Eye className="w-4 h-4 shrink-0" aria-hidden />
        ) : (
          <EyeOff className="w-4 h-4 shrink-0" aria-hidden />
        )}
      </div>

      <div className="space-y-5">
        {localPoll.questions.map((question, questionIndex) => {
          const picked = pickedOn(question);
          const showResults = (hasEnded || isAnswered(question)) && !editing[question.id];
          const isVoting = votingQuestionId === question.id;
          const canSubmit = question.allowMultiple ? true : picked.length === 1;

          return (
            <div key={question.id}>
              <div className="font-medium text-md mb-1 whitespace-pre-line text-gray-800 dark:text-gray-100">
                {total > 1 && (
                  <span className="text-gray-400 dark:text-gray-500 mr-1">
                    {questionIndex + 1}.
                  </span>
                )}
                {question.text}
              </div>

              {question.allowMultiple && !showResults && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Pick one or more
                </p>
              )}

              <div className="space-y-3 mb-3 mt-2">
                {question.options.map((option) => {
                  const percentage = percentageOf(option, question);
                  const isChosen = (question.userVotedOptionIds ?? []).includes(option.id);
                  const isSelected = picked.includes(option.id);

                  return (
                    <div key={option.id} className="relative">
                      <div
                        className={`relative p-3 rounded-lg border ${
                          isSelected || (showResults && isChosen)
                            ? "border-blue-500 dark:border-blue-400"
                            : "border-gray-200 dark:border-gray-700"
                        } ${
                          showResults
                            ? "cursor-default"
                            : "cursor-pointer hover:border-blue-300 dark:hover:border-blue-500"
                        }`}
                        onClick={() => {
                          if (showResults) return;
                          toggleOption(question, option.id);
                        }}
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex items-center">
                            {showResults ? (
                              <div className="w-5 h-5 mr-2 flex items-center justify-center">
                                {isChosen && <Check className="h-5 w-5 text-blue-500" />}
                              </div>
                            ) : (
                              <div
                                className={`w-5 h-5 border-2 mr-3 flex items-center justify-center ${
                                  question.allowMultiple ? "rounded" : "rounded-full"
                                } ${
                                  isSelected
                                    ? "border-blue-500 bg-blue-500"
                                    : "border-gray-300 dark:border-gray-600"
                                }`}
                              >
                                {isSelected &&
                                  (question.allowMultiple ? (
                                    <Check className="w-3 h-3 text-white" />
                                  ) : (
                                    <div className="w-2 h-2 rounded-full bg-white"></div>
                                  ))}
                              </div>
                            )}
                            <span className="text-sm">{option.text}</span>
                          </div>

                          {showResults && (
                            <span className="text-sm font-medium">{percentage}%</span>
                          )}
                        </div>
                      </div>

                      {showResults && (
                        <div
                          className="absolute top-0 left-0 h-full rounded-lg opacity-20 pointer-events-none"
                          style={{
                            width: `${percentage}%`,
                            backgroundColor: isChosen ? "#3b82f6" : "#9ca3af",
                          }}
                        ></div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex items-center justify-between gap-3">
                {showResults ? (
                  <>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {question.respondents}{" "}
                      {question.respondents === 1 ? "person answered" : "people answered"}
                    </span>
                    {!hasEnded && isAnswered(question) && (
                      <button
                        type="button"
                        className="text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
                        onClick={() => {
                          setSelected((current) => ({
                            ...current,
                            [question.id]: question.userVotedOptionIds ?? [],
                          }));
                          setEditing((current) => ({ ...current, [question.id]: true }));
                        }}
                      >
                        Change answer
                      </button>
                    )}
                  </>
                ) : (
                  <Button onClick={() => handleVote(question)} disabled={isVoting || !canSubmit}>
                    {isVoting ? "Submitting..." : question.allowMultiple ? "Save answer" : "Vote"}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-between items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mt-4 pt-3 border-t border-gray-200 dark:border-gray-700/60">
        <div>
          {localPoll.respondents ?? 0}{" "}
          {(localPoll.respondents ?? 0) === 1 ? "person answered" : "people answered"}
        </div>
        <div className="flex items-center gap-3">
          {namesVisible && (localPoll.respondents ?? 0) > 0 && (
            <button
              type="button"
              className="font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
              onClick={() => setShowVoters(true)}
            >
              See votes
            </button>
          )}
          <span>{hasEnded ? "Poll ended" : `Ends ${toDate(localPoll.endsAt).toLocaleString()}`}</span>
        </div>
      </div>

      {showVoters && (
        <PollVotersModal
          poll={localPoll}
          isOpen={showVoters}
          onClose={() => setShowVoters(false)}
        />
      )}
    </div>
  );
}
